import { useState } from "react";
import { Pencil, Trash2, ChevronDown, ChevronUp, Stethoscope, ClipboardList } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { formatRiskBadge, summarizeConclusion, formatExamDate } from "@/lib/formatters/examFormatter";

interface ReportRecord {
  type: string;
  report_type?: string;
  specialty?: string;
  date?: string;
  clinical_history?: string;
  findings?: string;
  conclusion?: string;
  recommendations?: string;
  physician_name?: string;
  physician_crm?: string;
  risk?: string;
  [key: string]: any;
}

interface ReportCardProps {
  record: ReportRecord;
  onUpdate: (updated: ReportRecord) => void;
  onDelete: () => void;
}

export function ReportCard({ record, onUpdate, onDelete }: ReportCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editForm, setEditForm] = useState(record);

  const riskBadge = formatRiskBadge(record.risk);
  const summaryConclusion = summarizeConclusion(record.conclusion || '', 200);

  const handleSave = () => {
    onUpdate(editForm);
    setIsEditing(false);
  };

  const handleDelete = () => {
    onDelete();
    setIsDeleting(false);
  };

  return (
    <>
      <Card className="overflow-hidden">
        <CardHeader className="pb-3 bg-muted/30">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="p-2 bg-background rounded-md shrink-0">
                <ClipboardList className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base leading-tight mb-1">
                  {record.report_type || 'Laudo Médico'}
                </h3>
                {record.specialty && (
                  <p className="text-sm text-muted-foreground">
                    {record.specialty}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {record.date && (
                    <span className="text-xs text-muted-foreground">
                      {formatExamDate(record.date)}
                    </span>
                  )}
                  <Badge variant={riskBadge.variant} className="text-xs">
                    {riskBadge.emoji} {riskBadge.label}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="h-8 w-8 p-0"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsDeleting(true)}
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-4 space-y-4">
          {/* Conclusão em Destaque */}
          {summaryConclusion && (
            <div className="bg-muted/50 rounded-lg p-3 border border-border/50">
              <div className="flex items-start gap-2">
                <span className="text-success font-bold text-lg shrink-0">✓</span>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                    Conclusão
                  </p>
                  <p className="text-sm leading-relaxed">
                    {summaryConclusion}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Recomendações */}
          {record.recommendations && (
            <div className="bg-accent/30 rounded-lg p-3 border border-accent/50">
              <div className="flex items-start gap-2">
                <span className="text-lg shrink-0">💊</span>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                    Recomendações
                  </p>
                  <p className="text-sm leading-relaxed">
                    {record.recommendations}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Médico */}
          {(record.physician_name || record.physician_crm) && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
              <Stethoscope className="h-3.5 w-3.5" />
              <span>
                {record.physician_name}
                {record.physician_crm && ` - CRM ${record.physician_crm}`}
              </span>
            </div>
          )}

          {/* Detalhes Técnicos (Colapsável) */}
          {(record.clinical_history || record.findings) && (
            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-between text-xs h-8"
                >
                  <span className="flex items-center gap-1.5">
                    🔍 Ver detalhes completos
                  </span>
                  {isExpanded ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 pt-3">
                {record.clinical_history && (
                  <div className="text-xs space-y-1">
                    <p className="font-semibold text-muted-foreground">Histórico Clínico:</p>
                    <p className="text-foreground/80 leading-relaxed whitespace-pre-wrap">
                      {record.clinical_history}
                    </p>
                  </div>
                )}
                {record.findings && (
                  <div className="text-xs space-y-1">
                    <p className="font-semibold text-muted-foreground">Achados:</p>
                    <p className="text-foreground/80 leading-relaxed whitespace-pre-wrap">
                      {record.findings}
                    </p>
                  </div>
                )}
                {record.conclusion && (
                  <div className="text-xs space-y-1">
                    <p className="font-semibold text-muted-foreground">Conclusão Completa:</p>
                    <p className="text-foreground/80 leading-relaxed whitespace-pre-wrap">
                      {record.conclusion}
                    </p>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}
        </CardContent>
      </Card>

      {/* Modal de Edição */}
      <AlertDialog open={isEditing} onOpenChange={setIsEditing}>
        <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Editar Laudo Médico</AlertDialogTitle>
            <AlertDialogDescription>
              Ajuste os campos do laudo conforme necessário.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Laudo</Label>
                <Input
                  value={editForm.report_type || ''}
                  onChange={(e) => setEditForm({ ...editForm, report_type: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Especialidade</Label>
                <Input
                  value={editForm.specialty || ''}
                  onChange={(e) => setEditForm({ ...editForm, specialty: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data</Label>
                <Input
                  type="date"
                  value={editForm.date || ''}
                  onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Risco</Label>
                <Input
                  value={editForm.risk || ''}
                  onChange={(e) => setEditForm({ ...editForm, risk: e.target.value })}
                  placeholder="baixo, moderado, alto"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Histórico Clínico</Label>
              <Textarea
                value={editForm.clinical_history || ''}
                onChange={(e) => setEditForm({ ...editForm, clinical_history: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Achados</Label>
              <Textarea
                value={editForm.findings || ''}
                onChange={(e) => setEditForm({ ...editForm, findings: e.target.value })}
                rows={5}
              />
            </div>
            <div className="space-y-2">
              <Label>Conclusão</Label>
              <Textarea
                value={editForm.conclusion || ''}
                onChange={(e) => setEditForm({ ...editForm, conclusion: e.target.value })}
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <Label>Recomendações</Label>
              <Textarea
                value={editForm.recommendations || ''}
                onChange={(e) => setEditForm({ ...editForm, recommendations: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Médico</Label>
                <Input
                  value={editForm.physician_name || ''}
                  onChange={(e) => setEditForm({ ...editForm, physician_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>CRM</Label>
                <Input
                  value={editForm.physician_crm || ''}
                  onChange={(e) => setEditForm({ ...editForm, physician_crm: e.target.value })}
                />
              </div>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSave}>Salvar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Confirmação de Exclusão */}
      <AlertDialog open={isDeleting} onOpenChange={setIsDeleting}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este laudo? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
